export const serviceCaseStudyPages = [
  {
    slug: "plant-factory-direct-meta-advertising",
    client: "Plant Factory Direct",
    title: "How We Generated 738 Meta Ad Link Clicks for Plant Factory Direct",
    description:
      "We managed seasonal Meta advertising for Plant Factory Direct, generating 738 link clicks at $0.10 per link click.",
    metaDescription:
      "We managed seasonal Meta advertising for Plant Factory Direct, generating 738 link clicks at $0.10 per link click.",
    path: "/case-study/plant-factory-direct-meta-advertising",
    stats: [
      {
        value: "738",
        label: "Reported link clicks",
      },
      {
        value: "$0.10",
        label: "Cost per link click",
      },
      {
        value: "5.59%",
        label: "CTR — all clicks",
      },
    ],
    overview: {
      eyebrow: "Overview",
      heading:
        "Advertising needed to reflect the realities of shipping live plants.",
      paragraphs: [
        "Plant Factory Direct sells live plants online. We managed its seasonal Meta advertising around customer demand and the store’s shipping schedule.",
        "We used link clicks, cost per link click, and click-through rate to track how shoppers responded to the ads.",
      ],
    },
    issue: {
      eyebrow: "The issue",
      heading: "Seasonal availability shaped when paid promotion made sense.",
      paragraphs: [
        "Live plants need suitable shipping conditions. Advertising needed to bring shoppers to the store while the business could fulfill their orders.",
        "We needed to coordinate campaign timing with seasonal availability and keep a close watch on the cost of attracting visitors.",
      ],
    },
    solution: {
      eyebrow: "The solution",
      heading: "We managed the campaign timing and reviewed the Meta results.",
      paragraphs: [
        "We ran Meta advertising during the opening part of December, then paused promotion around the store’s seasonal shipping constraints.",
        "We monitored link clicks and their cost to assess traffic generation. We also reviewed the all-click click-through rate to understand engagement with the ads.",
      ],
      highlights: [
        "We coordinated Meta campaigns with seasonal shipping availability.",
        "We monitored link clicks and cost per link click.",
        "We reviewed ad engagement and adjusted campaign timing.",
      ],
    },
    outcome: {
      eyebrow: "The outcome",
      heading: "We generated 738 link clicks at $0.10 each.",
      paragraphs: [
        "Our Meta campaigns generated 738 link clicks at an average cost of $0.10 per link click. The ads achieved a 5.59% all-click click-through rate.",
        "We paired that traffic generation with a planned seasonal pause, keeping promotion aligned with the store’s ability to ship live plants.",
      ],
    },
    services: [
      "Meta advertising management",
      "Seasonal campaign planning",
      "Paid social reporting",
      "Advertising and fulfillment coordination",
    ],
    relatedServices: [
      {
        href: "/social-media-marketing",
        label: "Social Media Marketing",
      },
    ],
    exhibit: {
      format: "facts",
      title: "738 link clicks at $0.10 each",
      period: "December 2025 campaign report",
      unit: "Meta advertising results",
      rows: [
        ["Link clicks", "738"],
        ["Cost per link click", "$0.10"],
        ["Click-through rate (all clicks)", "5.59%"],
      ],
      note: "Meta’s all-click CTR includes ad interactions as well as clicks through to the website.",
    },
    cta: {
      heading: "Need advertising that fits your business calendar?",
      description:
        "We can plan and manage paid social around your products, customer demand, and operational needs.",
      primaryLabel: "Discuss your project",
      primaryHref: "/contact-us",
    },
  },
  {
    slug: "motod-racing-speed-accessibility",
    client: "MotoD Racing",
    title: "How We Improved MotoD Racing’s Storefront Speed and Accessibility",
    description:
      "We improved MotoD Racing’s performance scores from 23 to 61 on mobile and 39 to 96 on desktop, and raised automated accessibility scores to 100 on both.",
    metaDescription:
      "We improved MotoD Racing’s performance scores from 23 to 61 on mobile and 39 to 96 on desktop, and raised automated accessibility scores to 100 on both.",
    path: "/case-study/motod-racing-speed-accessibility",
    stats: [
      {
        value: "23 → 61",
        label: "Mobile performance",
      },
      {
        value: "39 → 96",
        label: "Desktop performance",
      },
      {
        value: "93 → 100",
        label: "Mobile accessibility",
      },
      {
        value: "81 → 100",
        label: "Desktop accessibility",
      },
    ],
    overview: {
      eyebrow: "Overview",
      heading:
        "The first screen and interactive elements needed focused attention.",
      paragraphs: [
        "MotoD Racing’s storefront needed faster loading and more accessible navigation. We examined image and font loading, third-party tools, layout stability, and interactive controls.",
        "We tested the storefront on mobile and desktop before making changes, then repeated the tests to measure the results.",
      ],
    },
    issue: {
      eyebrow: "The issue",
      heading:
        "Loading behavior and interface barriers affected the tested storefront.",
      paragraphs: [
        "Our initial tests returned performance scores of 23 on mobile and 39 on desktop. We found opportunities to improve the main carousel image, image sizing, font loading, and the timing of third-party tools.",
        "We also found missing accessible link names, heading-order issues, incomplete image descriptions, and focus problems in hidden search controls and carousel slides.",
      ],
    },
    solution: {
      eyebrow: "The solution",
      heading:
        "We improved loading priorities and repaired the interface issues.",
      paragraphs: [
        "We prioritized the main carousel image, added image dimensions, improved font loading, and deferred selected third-party tools. Layout changes addressed carousel sizing and related storefront behavior.",
        "We corrected headings, accessible link names, image descriptions, and iframe labels. We also repaired focus behavior and accessibility states in hidden search controls and carousel slides.",
      ],
      highlights: [
        "We prioritized the main carousel image.",
        "We added image dimensions and improved font loading.",
        "We deferred selected third-party tools until needed.",
        "We repaired heading structure and accessible names.",
        "We corrected hidden-control and carousel focus behavior.",
        "We checked related navigation and cart compatibility.",
      ],
    },
    outcome: {
      eyebrow: "The outcome",
      heading: "We raised desktop performance from 39 to 96.",
      paragraphs: [
        "After our changes, mobile performance increased from 23 to 61 and desktop performance increased from 39 to 96. We measured the baseline on July 2 and retested on July 7, 2026.",
        "We also raised automated accessibility scores from 93 to 100 on mobile and from 81 to 100 on desktop by correcting the interface issues we identified.",
      ],
    },
    services: [
      "Website speed optimization",
      "Image and font loading",
      "Third-party script loading",
      "Accessibility remediation",
      "Semantic structure and focus behavior",
    ],
    relatedServices: [
      {
        href: "/website-speed-optimization",
        label: "Website Speed Optimization",
      },
      {
        href: "/ada-accessibility-optimization",
        label: "Accessibility Optimization",
      },
      {
        href: "/ecommerce-website-maintenance",
        label: "Ecommerce Website Maintenance",
      },
    ],
    exhibit: {
      format: "facts",
      title: "Before and after the storefront fixes",
      period: "July 2 → July 7, 2026",
      unit: "Automated test scores · out of 100",
      rows: [
        ["Mobile performance", "23 → 61"],
        ["Desktop performance", "39 → 96"],
        ["Mobile accessibility", "93 → 100"],
        ["Desktop accessibility", "81 → 100"],
      ],
      note: "Automated accessibility scores measure the checks covered by the test; they are not an ADA compliance certification.",
    },
    cta: {
      heading: "Is your storefront slow or difficult to use?",
      description:
        "We can identify loading bottlenecks and interface barriers, then make and verify targeted improvements.",
      primaryLabel: "Discuss your project",
      primaryHref: "/contact-us",
    },
  },
  {
    slug: "allpedal-google-shopping",
    client: "AllPedal",
    title: "How We Helped AllPedal Activate 129 Google Shopping Feed Items",
    description:
      "We addressed AllPedal’s Merchant Center setup and helped 129 of 130 feed items become active for Google Shopping.",
    metaDescription:
      "We addressed AllPedal’s Merchant Center setup and helped 129 of 130 feed items become active for Google Shopping.",
    path: "/case-study/allpedal-google-shopping",
    stats: [
      {
        value: "129",
        label: "Active Shopping feed items",
      },
      {
        value: "130",
        label: "Total feed items",
      },
      {
        value: "1",
        label: "Item still disapproved",
      },
    ],
    overview: {
      eyebrow: "Overview",
      heading:
        "The Shopping campaign needed products that were eligible to serve.",
      paragraphs: [
        "AllPedal’s Google Shopping campaign needed eligible products before its ads could reach shoppers. We reviewed the advertising account and Merchant Center setup to identify what was blocking approval.",
        "We focused on store-policy information and product eligibility so AllPedal could begin appearing in Shopping results.",
      ],
    },
    issue: {
      eyebrow: "The issue",
      heading:
        "Missing account information stood in the way of product eligibility.",
      paragraphs: [
        "We found missing return-policy information and shipping requirements that needed attention in Merchant Center. The unapproved products prevented the Shopping campaign from serving as intended.",
        "We needed to address those account requirements and monitor the feed as Google reviewed the products.",
      ],
    },
    solution: {
      eyebrow: "The solution",
      heading:
        "We worked on Merchant Center requirements and monitored approval.",
      paragraphs: [
        "We reviewed the Shopping setup and added return-policy information that matched the store’s website. We identified missing shipping information for follow-up and monitored product approval.",
        "As products became eligible, we checked Merchant Center activity and verified that AllPedal appeared in sponsored Shopping results.",
      ],
      highlights: [
        "We reviewed Merchant Center requirements and product eligibility.",
        "We added return-policy information that matched the store.",
        "We identified shipping information that needed attention.",
        "We monitored product approval and checked sponsored Shopping listings.",
      ],
    },
    outcome: {
      eyebrow: "The outcome",
      heading: "We helped activate 129 of 130 Shopping feed items.",
      paragraphs: [
        "By February 14, 2023, Merchant Center showed 129 active feed items out of 130 total. One item remained disapproved.",
        "AllPedal’s products appeared in sponsored Shopping results, and the campaign began receiving clicks. Resolving the eligibility barriers made those listings available to shoppers.",
      ],
    },
    services: [
      "Google Shopping eligibility troubleshooting",
      "Merchant Center return-policy setup",
      "Product approval monitoring",
      "Shopping campaign reporting",
    ],
    relatedServices: [
      {
        href: "/google-shopping-management",
        label: "Google Shopping Management",
      },
      {
        href: "/google-merchant-center-services",
        label: "Google Merchant Center Services",
      },
    ],
    exhibit: {
      format: "facts",
      title: "129 of 130 feed items active",
      period: "February 14, 2023",
      unit: "Google Merchant Center · Shopping feed status",
      rows: [
        ["Active feed items", "129"],
        ["Total feed items", "130"],
        ["Disapproved feed items", "1"],
      ],
      note: "Feed items include product variants.",
    },
    cta: {
      heading: "Are product approvals holding up your Shopping ads?",
      description:
        "We can diagnose product disapprovals and address the feed and account issues preventing your listings from reaching shoppers.",
      primaryLabel: "Discuss your project",
      primaryHref: "/contact-us",
    },
  },
];
